import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, Icon, Modal, message, WkdUploadCore } from '@common/musicHubCommon';
import type { UploadFileInfo } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { useGenreStore, useMoodStore, useUserStore  } from '../../../store';
import { reportPageView } from '../../../services/reportLog';
import './index.scss';

const MUSIC_BIG_ICON = 'https://akoss.bangcdn.net/fe/MusicBig.svg';

interface AudioFileInfo {
  name: string;
  size: number;
  type: string;
}

interface BasicFormData {
  songTitle: string;
  coverArtPreview: string | null;
  featuredArtists: string[];  // Featured Artists（非必填）
  producer: string;
  genres: string[];
  moods: string[];
}

interface AlbumData {
  albumName: string;
  coverArtPreview: string | null;
  artists: string[];
  genres: string[];
}

const ReleaseBasicInfo: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isCoverHovered, setIsCoverHovered] = useState(false);
  const [showAlbumDialog, setShowAlbumDialog] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const userInfo = useUserStore((state) => state.user);
  // 从 store 获取 genre 数据
  const genres = useGenreStore((state) => state.genreOptions);
  const moods = useMoodStore((state: any) => state.moodOptions);

  // 从 sessionStorage 获取发布类型和专辑信息
  const releaseType = sessionStorage.getItem('releaseType') || 'song';
  const isAlbumMode = releaseType === 'album';
  
  const albumData: AlbumData | null = useMemo(() => {
    const stored = sessionStorage.getItem('albumData');
    return stored ? JSON.parse(stored) : null;
  }, []);

  // 从 sessionStorage 获取音频文件信息
  const audioFileInfo: AudioFileInfo | null = useMemo(() => {
    const stored = sessionStorage.getItem('releaseAudioFile');
    return stored ? JSON.parse(stored) : null;
  }, []);

  // 表单数据 - 专辑模式下自动使用专辑封面
  const [formData, setFormData] = useState<BasicFormData>(() => ({
    songTitle: '',
    coverArtPreview: isAlbumMode && albumData ? albumData.coverArtPreview : null,
    featuredArtists: [],  // Featured Artists 初始为空
    producer: '',
    genres: isAlbumMode && albumData ? albumData.genres : [''],
    moods: [''],
  }));

  useEffect(() => {
    reportPageView('content_0013', { withArtistId: true });
  }, []);

  // 如果没有音频文件，返回上传页面
  useEffect(() => {
    if (!audioFileInfo) {
      navigate(ROUTES.releaseUpload);
    }
  }, [audioFileInfo, navigate]);

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.releaseUpload);
  };

  // 封面上传前 - 启动 loading
  const handleBeforeCoverUpload = () => {
    setCoverUploading(true);
    return Promise.resolve();
  };

  // 处理封面上传成功
  const handleCoverUpload = (fileInfo: UploadFileInfo) => {
    setCoverUploading(false);
    if (fileInfo?.url) {
      setFormData(prev => ({
        ...prev,
        coverArtPreview: fileInfo.url,
      }));
    }
  };

  // 处理封面上传失败
  const handleCoverUploadFail = (error: any) => {
    setCoverUploading(false);
    message.error(String(error));
  };

  // 处理 Featured Artists 列表
  const handleAddFeaturedArtist = () => {
    setFormData(prev => ({
      ...prev,
      featuredArtists: [...prev.featuredArtists, ''],
    }));
  };

  const handleFeaturedArtistChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      featuredArtists: prev.featuredArtists.map((a, i) => (i === index ? value : a)),
    }));
  };

  const handleRemoveFeaturedArtist = (index: number) => {
    setFormData(prev => ({
      ...prev,
      featuredArtists: prev.featuredArtists.filter((_, i) => i !== index),
    }));
  };

  // 处理流派列表
  const handleGenreChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.map((g, i) => (i === index ? value : g)),
    }));
  };

  const handleAddGenre = () => {
    setFormData(prev => ({
      ...prev,
      genres: [...prev.genres, ''],
    }));
  };

  const handleRemoveGenre = (index: number) => {
    if (formData.genres.length > 1) {
      setFormData(prev => ({
        ...prev,
        genres: prev.genres.filter((_, i) => i !== index),
      }));
    }
  };

  // 处理情绪列表
  const handleMoodChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      moods: prev.moods.map((m, i) => (i === index ? value : m)),
    }));
  };

  const handleAddMood = () => {
    setFormData(prev => ({
      ...prev,
      moods: [...prev.moods, ''],
    }));
  };

  const handleRemoveMood = (index: number) => {
    if (formData.moods.length > 1) {
      setFormData(prev => ({
        ...prev,
        moods: prev.moods.filter((_, i) => i !== index),
      }));
    }
  };

  // 表单验证（Featured Artists 非必填）
  const isFormValid = () => {
    const validGenres = formData.genres.filter(g => g.trim() !== '');
    return (
      formData.songTitle.trim() !== '' &&
      formData.coverArtPreview !== null &&
      validGenres.length > 0
    );
  };

  // 保存歌曲到专辑
  const saveSongToAlbum = () => {
    // 获取现有的专辑歌曲列表
    const existingSongsStr = sessionStorage.getItem('albumSongs');
    const existingSongs = existingSongsStr ? JSON.parse(existingSongsStr) : [];
    
    // 创建新歌曲数据
    const newSong = {
      id: `song-${Date.now()}`,
      songTitle: formData.songTitle,
      coverArtPreview: formData.coverArtPreview,
      featuredArtists: formData.featuredArtists.filter(a => a.trim() !== ''),
      producer: formData.producer,
      genres: formData.genres.filter(g => g.trim() !== ''),
      moods: formData.moods.filter(m => m.trim() !== ''),
      audioFile: audioFileInfo,
    };
    
    // 添加到列表
    const updatedSongs = [...existingSongs, newSong];
    sessionStorage.setItem('albumSongs', JSON.stringify(updatedSongs));
    
    // 清除当前音频文件信息
    sessionStorage.removeItem('releaseAudioFile');
    
    return updatedSongs;
  };

  // 处理下一步
  const handleNext = () => {
    if (isFormValid()) {
      if (isAlbumMode) {
        // 专辑模式：显示对话框让用户选择继续添加或完成
        setShowAlbumDialog(true);
      } else {
        // 单曲模式：继续到高级选项
        sessionStorage.setItem('releaseBasicInfo', JSON.stringify(formData));
        navigate(ROUTES.releaseAdvanced);
      }
    }
  };

  // 处理添加更多歌曲
  const handleAddAnotherSong = () => {
    saveSongToAlbum();
    setShowAlbumDialog(false);
    // 跳转回上传页面
    navigate(ROUTES.releaseUpload);
  };

  // 处理完成添加
  const handleDoneAddingSongs = () => {
    saveSongToAlbum();
    setShowAlbumDialog(false);
    // 跳转到编辑排序页面
    navigate(ROUTES.albumEditReorder);
  };

  if (!audioFileInfo) {
    return null;
  }

  return (
    <div className="release-basic">
      {/* 页面标题 */}
      <div className="release-basic__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Release Song</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="release-basic__content">
        <div className="release-basic__card">
          {/* 已上传的音频文件信息 */}
          <div className="audio-file-info">
            <div className="audio-file-info__icon">
              <img src={MUSIC_BIG_ICON} alt="" />
            </div>
            <div className="audio-file-info__text">
              <span className="audio-file-info__name">{audioFileInfo.name}</span>
              <span className="audio-file-info__status">Upload completed</span>
            </div>
          </div>

          {/* 标题 */}
          <h2 className="release-basic__title">Basic Information</h2>

          {/* 表单 */}
          <Form form={form} layout="vertical" className="release-basic__form">
            {/* 歌曲标题 */}
            <Form.Item 
              label="Song Title" 
              required
              className="form-item"
            >
              <Input
                value={formData.songTitle}
                onChange={(e) => setFormData(prev => ({ ...prev, songTitle: e.target.value }))}
                placeholder="Enter song title"
              />
            </Form.Item>

            {/* 封面上传 */}
            <Form.Item 
              label="Cover Art" 
              required
              className="form-item"
            >
              <WkdUploadCore
                accept="image/jpeg,image/jpg,image/png"
                size={10 * 1024}
                // ratio={1}
                onBeforeUpload={handleBeforeCoverUpload}
                onChange={handleCoverUpload}
                onFaild={handleCoverUploadFail}
              >
                <div
                  className={`cover-upload ${isCoverHovered ? 'cover-upload--hover' : ''}`}
                  onMouseEnter={() => setIsCoverHovered(true)}
                  onMouseLeave={() => setIsCoverHovered(false)}
                >
                  {coverUploading ? (
                    <div className="cover-upload__loading">
                      <div className="cover-upload__spinner" />
                    </div>
                  ) : formData.coverArtPreview ? (
                    <img src={formData.coverArtPreview} alt="Cover art" className="cover-upload__preview" />
                  ) : (
                    <div className="cover-upload__placeholder">
                      <img src="https://akoss.bangcdn.net/fe/Upload.svg" alt="" />
                    </div>
                  )}
                </div>
              </WkdUploadCore>
              <p className="form-item__hint">Min 1000x1000 pixels, JPG or PNG. Maximum 10 MB.</p>
            </Form.Item>

            {/* Artist *：主艺名（不可编辑）+ Featured Artists */}
            <Form.Item 
              label="Artist"
              required
              className="form-item form-item--artist"
            >
              <div className="artist-fields">
                <Input
                  value={userInfo?.name}
                  placeholder="Artist name"
                  disabled
                  className="artist-fields__main"
                />
                {formData.featuredArtists.map((artist, index) => (
                  <div key={index} className="form-item__row artist-fields__row">
                    <Input
                      value={artist}
                      onChange={(e) => handleFeaturedArtistChange(index, e.target.value)}
                      placeholder="Enter featured artist name"
                    />
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveFeaturedArtist(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  </div>
                ))}
                <button type="button" className="add-btn" onClick={handleAddFeaturedArtist}>
                  <span>+</span> Add Featured Artist
                </button>
              </div>
            </Form.Item>

            {/* 制作人 */}
            <Form.Item 
              label="Producer"
              className="form-item"
            >
              <Input
                value={formData.producer}
                onChange={(e) => setFormData(prev => ({ ...prev, producer: e.target.value }))}
                placeholder="Producer name"
              />
            </Form.Item>

            {/* 流派 */}
            <Form.Item 
              label="Genre" 
              required
              className="form-item"
            >
              {formData.genres.map((genre, index) => (
                <div key={index} className="form-item__row">
                  <Select
                    value={genre || undefined}
                    onChange={(value) => handleGenreChange(index, value as string)}
                    placeholder="Select genre"
                    options={genres.filter(
                      g => !formData.genres.includes(g.value) || g.value === genre
                    )}
                    style={{ width: '100%' }}
                  />
                  {formData.genres.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveGenre(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddGenre}>
                <span>+</span> Add Genre
              </button>
            </Form.Item>

            {/* 情绪 */}
            <Form.Item 
              label="Song Mood"
              className="form-item"
            >
              {formData.moods.map((mood, index) => (
                <div key={index} className="form-item__row">
                  <Select
                    value={mood || undefined}
                    onChange={(value) => handleMoodChange(index, value as string)}
                    placeholder="Select mood"
                    options={moods.filter(
                      (m: any) => !formData.moods.includes(m.value) || m.value === mood
                    )}
                    style={{ width: '100%' }}
                  />
                  {formData.moods.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveMood(index)}
                    >
                      <Icon style="Close" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-btn" onClick={handleAddMood}>
                <span>+</span> Add Mood
              </button>
            </Form.Item>

            {/* 提交按钮 */}
            <Button
              type="primary"
              disabled={!isFormValid()}
              onClick={handleNext}
              className={`release-basic__submit ${!isFormValid() ? 'release-basic__submit--disabled' : ''}`}
            >
              {isAlbumMode ? 'Save Song' : 'Next'}
            </Button>
          </Form>
        </div>
      </div>

      {/* 专辑模式对话框 */}
      <Modal
        open={showAlbumDialog}
        onCancel={() => setShowAlbumDialog(false)}
        footer={null}
        centered
        closable={false}
        className="album-song-dialog"
        width={480}
      >
        <div className="dialog-content">
          <div className="dialog-icon">
            <img src={MUSIC_BIG_ICON} alt="" />
          </div>
          <h2 className="dialog-title">Song Added!</h2>
          <p className="dialog-subtitle">
            "{formData.songTitle}" has been added to your album.
          </p>
          <p className="dialog-desc">Would you like to add another song or finish?</p>
          
          <div className="dialog-actions">
            <Button type="primary" onClick={handleAddAnotherSong} className="dialog-btn dialog-btn--primary">
              Add Another Song
            </Button>
            <Button onClick={handleDoneAddingSongs} className="dialog-btn dialog-btn--secondary">
              Done, Review Album
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReleaseBasicInfo;
